import { services, artisans } from '../api/endpoints'
import {
  CustomPaginationQuery,
  ApiResponse,
  ArtisansResponse,
  EditArtisanReq,
  GetSingleArtisan,
  CreateArtisanReq,
  UpdateArtisanResData,
  DeleteArtisanPayload,
  DeleteArtisanResponse,
} from '../interfaces/services'

import useSWR from 'swr'
import { authCall } from '.'
interface ServiceListQuery extends CustomPaginationQuery {
  estateId?: string
  serviceId?: string
  search?: string
}

interface CreateServicePayload {
  name: string
  description: string
  estateId: string
}

const ArtisanService = {
  useGetServiceList: ({ estateId, page = 1, size = 15 }: ServiceListQuery) => {
    const url =
      estateId && estateId.trim()
        ? `${services(estateId)}?page=${page}&limit=${size}`
        : null

    return useSWR<ApiResponse>(
      url,
      async () => {
        if (!url) return null as any
        const res = await authCall<ApiResponse>({ url, method: 'get' })
        return res
      },
      {
        revalidateOnFocus: false,
        shouldRetryOnError: false,
      }
    )
  },

  useGetServiceById: ({
    estateId,
    serviceId,
    page = 1,
    size = 100,
  }: ServiceListQuery) => {
    const listUrl =
      estateId && estateId.trim()
        ? `${services(estateId)}?page=${page}&limit=${size}`
        : null

    const key = listUrl && serviceId ? `${listUrl}&fetchServiceId=${serviceId}` : null

    return useSWR<any>(
      key,
      async () => {
        if (listUrl === null) return null

        const res = await authCall<ApiResponse>({ url: listUrl!, method: 'get' })
        const list = (res?.results || []) as any[]
        const service = list.find((s: any) => s.id === serviceId) || null
        return service
      },
      {
        revalidateOnFocus: false,
        shouldRetryOnError: false,
      }
    )
  },

  useGetArtisans: ({ page = 1, size = 15, serviceId, search, estateId }: ServiceListQuery) => {
    const params = new URLSearchParams()
    params.append('page', String(page))
    params.append('limit', String(size))
    if (serviceId) params.append('serviceId', serviceId)
    if (search && search.trim().length) params.append('search', search.trim())

    const qs = params.toString()
    const key = qs ? `${artisans}?${qs}` : artisans

    return useSWR<ArtisansResponse | null>(
      key,
      async () => {
        const res = await authCall<ArtisansResponse>({ url: key, method: 'get' })
        const maybe = res as any
        return maybe.data ?? res
      },
      {
        revalidateOnFocus: false,
        shouldRetryOnError: false,
      }
    )
  },

  createService: async (payload: CreateServicePayload) => {
    const url = services(payload.estateId)
    const res = await authCall<ApiResponse>({
      url,
      method: 'post',
      data: {
        title: payload.name,
        description: payload.description,
      },
    })

    const maybe = res as any
    return maybe.data ?? res
  },

  createArtisan: async (payload: CreateArtisanReq) => {
    const url = `${artisans}`
    const res = await authCall<ArtisansResponse>({
      url,
      method: 'post',
      data: payload,
    })

    const maybe = res as any
    return maybe.data ?? res
  },
  deleteArtisan: async ({ artisanId, serviceId }: DeleteArtisanPayload) => {
    const url = `${artisans}/${artisanId}/services/${serviceId}`

    const res = await authCall<DeleteArtisanResponse>({
      url,
      method: 'delete',
    })

    return res
  },
  async getArtisanById(id: string): Promise<GetSingleArtisan> {
    const url = `${artisans}/${id}`
    const res = await authCall<GetSingleArtisan>({
      url,
      method: 'get',
    })
    return res
  },
  updateArtisan: async (artisanId: string, payload: EditArtisanReq) => {
    const url = `${artisans}/${artisanId}`

    const res = await authCall<UpdateArtisanResData>({
      url,
      method: 'patch',
      data: payload,
    })
    return res
  },

  uploadArtisanPhoto: (file: File) => {
    const formData = new FormData()
    formData.append('file', file)

    return authCall<{ success: boolean; message: string; data: { url: string } }>({
      url: `/uploads/file?category=artisans`,
      data: formData,
      method: 'post',
    })
  },
}

export default ArtisanService
